import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { catchError, finalize, map, Observable, switchMap, throwError, timer } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FileScanStatus } from '../financial-closing-process/enums/fileScanStatus.enum';

@Injectable({ providedIn: 'root' })
export class FileMetadataService {
    protected readonly fileMetadata = 'file-metadata';

    protected readonly apiGatewayUrl: string;

    constructor(protected readonly http: HttpClient, protected readonly coreProvider: AumicoCoreProvider) {
        this.apiGatewayUrl = environment.modeso.apiGateway;
    }

    downloadDocument$(documentId: string, documentName?: string, retryCount = 0, maxRetries = 3): Observable<Blob> {
        const url = `${this.apiGatewayUrl}${this.fileMetadata}/download/${documentId}`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
            map((response: HttpResponse<Blob>) => {
                const contentType = response.headers.get('Content-Type');
                const blob = new Blob([response.body], { type: contentType });

                return blob;
            }),
            catchError((error: HttpErrorResponse) => {
                const reason = error.error?.reason || 'UNKNOWN';

                if (reason === FileScanStatus.UNSCANNED && retryCount < maxRetries) {
                    return timer(2000).pipe(
                        switchMap(() => this.downloadDocument$(documentId, documentName, retryCount + 1, maxRetries)),
                    );
                }

                if (reason === FileScanStatus.QUARANTINED) {
                    return throwError(
                        () => new Error('The file is quarantined due to a virus and cannot be downloaded.'),
                    );
                }

                if (retryCount >= maxRetries) {
                    return throwError(() => new Error('Maximum retry attempts reached. Please try again later.'));
                }

                return throwError(() => new Error('An unexpected error occurred while downloading the file.'));
            }),
        );
    }

    deleteDocument$(documents: string[]): Observable<void> {
        this.coreProvider.dispatchBlockingRequestEnd('deleteDocument$');
        const url = `${this.apiGatewayUrl}${this.fileMetadata}`;
        return this.http
            .delete<void>(url, { body: { uuid: documents } })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deleteDocument$')));
    }
}
